// "View as" lens — the admin-only trust check: see the BackOffice exactly as
// a member of chosen groups sees it. State lives in sessionStorage (per-tab,
// survives reload, never leaks to other tabs); while active, the shared HTTP
// client sends `x-view-as-groups` and the backend — only after verifying the
// REAL caller is owner/SUPER_ADMIN — serves every request narrowed to those
// groups. The lens can only narrow: a non-admin sending the header gains
// nothing.

const KEY = 'bldr-view-as';

export interface ViewAsState {
  /** The lens groups. Empty = a member of no groups (sees nothing) — valid. */
  groupIds: string[];
  /** Banner label, e.g. "Cool One Tag member" or an employee's name. */
  label: string;
}

export function getViewAs(): ViewAsState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.groupIds)) return null;
    return { groupIds: parsed.groupIds.map(String), label: String(parsed.label ?? 'member') };
  } catch {
    return null;
  }
}

/** Header value while the lens is active; `-` marks "member of no groups". */
export function viewAsHeaderValue(): string | null {
  const state = getViewAs();
  if (!state) return null;
  return state.groupIds.length ? state.groupIds.join(',') : '-';
}

/** Enter the lens and reload so every surface refetches through it. */
export function startViewAs(state: ViewAsState): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    return;
  }
  window.location.reload();
}

/** Exit the lens and reload back to the admin's own view. */
export function stopViewAs(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    /* best effort */
  }
  window.location.reload();
}
